import * as ko from 'knockout';
import {IObjectData, IOverride, IRouteDefinitionParams} from '../../../index';
import {ISplitButtonParams} from '../../button/splitButton/split-button';
import {global, lm} from '../../../appContext';
import {nsRoute} from '../../../util/route';
import {getObjectFromRouteData} from '../../../util/request';
import {getNextId} from '../../../util/misc';
import {nsFa} from '../../fa/fn';
import {GroupMemberSearch} from './memberSearch';
import {nsGroupMemberOverride} from './nsGroupMemberOverride';
import {nsGroupMembers} from './nsGroupMembers';
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import TGroupMembersTab = nsGroupMemberOverride.TGroupMembersTab;
import collectOverrideTargetUserIds = nsGroupMembers.collectOverrideTargetUserIds;
import filterUserMembershipOverrides = nsGroupMemberOverride.filterUserMembershipOverrides;
import invalidateMembershipOverrideCaches = nsGroupMemberOverride.invalidateMembershipOverrideCaches;
import i = nsFa.i;
import detailsHtml from './details.html?raw';

export const template = detailsHtml;

const STATUS_FILTER_OPTIONS: ReadonlyArray<{tab: TGroupMembersTab; labelKey: string}> = [
    {tab: 'current', labelKey: 'group.members.tabs.current'},
    {tab: 'pending', labelKey: 'group.members.tabs.pending'},
    {tab: 'added', labelKey: 'group.members.tabs.added'},
    {tab: 'removed', labelKey: 'group.members.tabs.removed'},
];

export class viewModel extends ParsedRouteDataDecorator {
    readonly memberSearch: GroupMemberSearch;
    readonly membershipOverrides = ko.observableArray<IOverride>([]);
    readonly memberTab = ko.observable<TGroupMembersTab>('current');

    readonly statusFilterOptions = STATUS_FILTER_OPTIONS;
    readonly isStatusMenuOpen = ko.observable(false);
    readonly statusMenuId = `group-members-status-${getNextId()}`;
    readonly statusMenuPanelId = `${this.statusMenuId}-menu`;
    private readonly statusAnchorName = `--${this.statusMenuId}`;
    readonly statusAnchorStyle = {'anchor-name': this.statusAnchorName};
    readonly statusMenuStyle = {'position-anchor': this.statusAnchorName};

    readonly selectedStatusLabelKey = ko.pureComputed(() => {
        const tab = this.memberTab();
        return STATUS_FILTER_OPTIONS.find(o => o.tab === tab)?.labelKey
            ?? 'group.members.tabs.current';
    });

    readonly selectedStatusBadge = ko.pureComputed(() => {
        return this.memberSearch.tabBadges()[this.memberTab()];
    });

    constructor(params: IRouteDefinitionParams) {
        super(params);
        this.memberSearch = new GroupMemberSearch(
            params,
            this.membershipOverrides,
            () => this.refreshAfterOverrideMutation(),
            this.memberTab,
        );
        document.addEventListener('pointerdown', this.onStatusDocumentPointerDown, true);
        this.init();
    }

    onStatusToggleClick = (): void => {
        this.isStatusMenuOpen(!this.isStatusMenuOpen());
    };

    onSelectStatus = (tab: TGroupMembersTab): void => {
        this.memberTab(tab);
        this.isStatusMenuOpen(false);
    };

    onStatusToggleKeyDown = (_: unknown, event: KeyboardEvent): boolean => {
        if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.openStatusMenuAndFocus(0);
            return false;
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.openStatusMenuAndFocus(-1);
            return false;
        }
        if (event.key === 'Escape') {
            this.closeStatusMenuAndFocusToggle();
            return false;
        }
        return true;
    };

    onStatusMenuKeyDown = (_: unknown, event: KeyboardEvent): boolean => {
        const items = this.getStatusMenuItems();
        const currentIndex = items.indexOf(event.target as HTMLElement);
        let nextIndex: number | null = null;

        switch (event.key) {
            case 'ArrowDown':
                nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
                break;
            case 'ArrowUp':
                nextIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
                break;
            case 'Home':
                nextIndex = 0;
                break;
            case 'End':
                nextIndex = items.length - 1;
                break;
            case 'Escape':
                event.preventDefault();
                this.closeStatusMenuAndFocusToggle();
                return false;
            case 'Tab':
                this.isStatusMenuOpen(false);
                return true;
            default:
                return true;
        }

        event.preventDefault();
        items[nextIndex]?.focus();
        return false;
    };

    onStatusFocusOut = (_: unknown, event: FocusEvent): boolean => {
        const container = document.getElementById(this.statusMenuId);
        if (!container?.contains(event.relatedTarget as Node | null)) {
            this.isStatusMenuOpen(false);
        }
        return true;
    };

    private onStatusDocumentPointerDown = (event: PointerEvent): void => {
        if (!document.getElementById(this.statusMenuId)?.contains(event.target as Node)) {
            this.isStatusMenuOpen(false);
        }
    };

    private openStatusMenuAndFocus(index: number): void {
        this.isStatusMenuOpen(true);
        requestAnimationFrame(() => {
            const items = this.getStatusMenuItems();
            items[index < 0 ? items.length - 1 : index]?.focus();
        });
    }

    private closeStatusMenuAndFocusToggle(): void {
        this.isStatusMenuOpen(false);
        document.querySelector<HTMLElement>(`#${this.statusMenuId} > button`)?.focus();
    }

    private getStatusMenuItems(): HTMLElement[] {
        return Array.from(
            document.querySelectorAll<HTMLElement>(
                `#${this.statusMenuPanelId} > [role="menuitemradio"]:not(:disabled)`,
            ),
        );
    }

    /** Invalidate caches, reload group overrides, and refresh the members search table. */
    refreshAfterOverrideMutation(): Promise<void> {
        invalidateMembershipOverrideCaches();
        const groupRefresh = this.__requestRouteData
            ? this.__requestRouteData!.request().then((data) => this.applyGroupFromRouteData(data as [IObjectData]))
            : Promise.resolve();
        return groupRefresh.then(() => this.memberSearch.refreshMembers());
    }

    private applyGroupFromRouteData(data: [IObjectData]): void {
        const group = getObjectFromRouteData(data);
        this.memberSearch.setGroup(group);
        this.membershipOverrides(filterUserMembershipOverrides(group?.overrides ?? []));
    }

    private init(): void {
        const routeData = this.__requestRouteData?.request();
        if (!routeData) {
            void this.memberSearch.start();
            return;
        }
        routeData
            .then((data) => this.applyGroupFromRouteData(data as [IObjectData]))
            .finally(() => this.memberSearch.start());
    }

    canAddMembers(): boolean {
        return global.permissionValidator.isMinSuperManager();
    }

    /** Same chrome as `component/systemaccess/search` create collection button. */
    getAddMembersSplitButtonParams(): ISplitButtonParams | null {
        if (!this.canAddMembers() || !this.memberSearch.isMembersSearchReady()) {
            return null;
        }
        return {
            mainButton: {
                cls: 'btn-action btn-sm',
                icon: 'fa-plus',
                label: 'group.members.add',
                onClick: () => this.onAddMembersClick(),
                validator: () => this.canAddMembers(),
            },
        };
    }

    onAddMembersClick(): void {
        const group = this.memberSearch.getGroup();
        if (!group?.id || !this.canAddMembers()) {
            return;
        }
        const overrideTargetUserIds = collectOverrideTargetUserIds(this.membershipOverrides());
        nsGroupMembers.showManualMembershipEducationConfirm(() => {
            global.dialogManager.addComponent({
                title: `${i('fa-plus')} ((add)) ((${lm.t('user|p|ac')}))`,
                content: {
                    name: 'component/group/members/dialog/add-members',
                    params: {
                        group,
                        overrideTargetUserIds,
                        onComplete: () => this.refreshAfterOverrideMutation(),
                    },
                },
            });
        });
    }

    override dispose(): void {
        document.removeEventListener('pointerdown', this.onStatusDocumentPointerDown, true);
        this.selectedStatusLabelKey.dispose();
        this.selectedStatusBadge.dispose();
        this.__requestRouteData?.abort();
        this.memberSearch.dispose();
    }
}
